/**
 * GET /solar-proposal/v2/:proposalId/designer and the design save. The design
 * is the stored row in the shape the designer's `designSchema` parses; parse it
 * before use.
 */
export type SolarDesignerImageryDto = {
  id: string
  createdAt: string
  updatedAt: string
  source: string
  filename: string | null
  imageDate: string | null
  width: number | null
  height: number | null
  zoomLevel: number | null
  siteId: string | null
  positionedX: number | null
  positionedY: number | null
  positionedZoom: number | null
}

export type SolarDesignerSiteDto = {
  id: string
  address: string
  street: string
  locality: string
  state: string
  postalCode: string
  latitude: number
  longitude: number
  streetViewLatitude: number | null
  streetViewLongitude: number | null
  /** Displayable imagery only, poisoned rows filtered on the server. */
  imagery: SolarDesignerImageryDto[]
}

/** The design's module, in the shape `allEquipmentForSiteState` holds. */
export type SolarDesignerModuleEquipmentDto = {
  id: number
  name: string
  proposalName: null
  typeId: number
  currentProposalPrice: number
  description: string | null
  manufacturerId: number
  manufacturer: string
  catalogNumber: string | null
  isKit: boolean
  isBattery: false
  isDefault: false
  isDomesticContent: boolean
  batteryData: null
  evChargerData: null
  wattage: number | null
  height: number | null
  width: number | null
}

export type SolarDesignerProposalDto = {
  id: string
  sellerId: string
  mentorId: string | null
  systemType: string | null
  homeowner: { firstName: string; lastName: string }
  annualEnergyUseKwh: number | null
}

export type SolarDesignerInstallAreaProductionDto = {
  installAreaId: string
  acAnnualKwh: number
}

export type SolarDesignerSummaryDto = {
  systemSizeKw: number | null
  panelCount: number | null
  annualProductionKwh: number | null
  /** A whole percent: 102 means 102%. */
  offsetPct: number | null
}

export type SolarDesignerUnavailableReason =
  | `aurora_design`
  | `module_missing`
  | `no_active_design`

export type SolarDesignerAvailableDto = {
  status: `available`
  design: unknown
  /** The version a save sends back as `expectedUpdatedAt`. */
  updatedAt: string
  site: SolarDesignerSiteDto
  equipment: SolarDesignerModuleEquipmentDto[]
  proposal: SolarDesignerProposalDto
  installAreaProduction: SolarDesignerInstallAreaProductionDto[]
  summary: SolarDesignerSummaryDto
  viewerCanUpdate: boolean
  viewerCanResetDesign: boolean
}

export type SolarDesignerUnavailableDto = {
  status: `unavailable`
  reason: SolarDesignerUnavailableReason
  /** Seller-facing. */
  message: string
  viewerCanUpdate: boolean
}

export type SolarDesignerBundleDto =
  | SolarDesignerAvailableDto
  | SolarDesignerUnavailableDto

export type SolarDesignSaveWarning =
  | `equipment_removed_incompatible`
  | `production_not_updated`

export type SolarDesignSaveResultDto = {
  design: unknown
  updatedAt: string
  installAreaProduction: SolarDesignerInstallAreaProductionDto[]
  summary: SolarDesignerSummaryDto
  warnings: SolarDesignSaveWarning[]
  productionNotUpdatedReason: string | null
}

/** The fields a designer save may send; equipment belongs to the Equipment step. */
export const SOLAR_DESIGN_SAVE_FIELDS = [
  `nodes`,
  `edges`,
  `installAreas`,
  `installAreasToPanels`,
  `panels`,
  `obstructions`,
  `shadingAreas`,
  `setbacks`,
  `phase`,
  `tool`,
  `completedAt`,
  `displayImageryProvider`,
  `brochurePositionX`,
  `brochurePositionY`,
  `brochureZoom`,
  `homeownerRebate`,
  `homeownerRebateAmount`,
  `roofWorkRebate`,
  `roofWorkRebateAmount`,
  `treeTrimmingRebate`,
  `treeTrimmingRebateAmount`,
  `energyEfficiencyKitRebate`,
  `energyEfficiencyKitRebateAmount`,
] as const

export type SolarDesignSaveField = (typeof SOLAR_DESIGN_SAVE_FIELDS)[number]

export type SolarDesignSaveBody = Partial<
  Record<SolarDesignSaveField, unknown>
> & { expectedUpdatedAt: string }
